import { StorageKeys } from "@components/Data/enums";
import CorporateLeadForm from "@components/LeadForms/CorporateLeadForm";
import FiveLeadForm from "@components/LeadForms/FiveLeadForm";
import ThreeLeadForm from "@components/LeadForms/ThreeLeadForm";
import TwoLeadForm from "@components/LeadForms/TwoLeadForm";
import { useLocalStorageNew } from "@components/hooks/utils/localStorageNew";
import {
    useGetCities,
    useGetUserIP,
    usePhoneGenerateOTP,
    usePhoneValidateOTP,
    usePostKyc
} from "@hooks/api/kyc";
import { getAge } from "@hooks/utils/date";
import { getCookie } from "@hooks/utils/helpers";
import { useRouter } from "next/router";
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from "react";
import { defaultCountry } from "../../components/health-insurance-plan/summary/data";
import { LeadFormNames, LeadFormTypes } from "./enums";
import { constraints, nameRegex, phoneRegex, validPhoneRegex } from "./helper";

declare global {
    interface CredentialRequestOptions {
        otp?: { transport: string[] };
    }
}
declare global {
    interface LmSMTObj {
        page: (
            pageName: string,
            pageData: Record<string, any>,
            pageOptions: any[]
        ) => void;
        track: (pageName: string, pageData: Record<string, any>) => void;
        identify: (
            pageName: string,
            pageData: Record<string, any>,
            pageData2?: Record<string, any>
        ) => void;
    }
    interface Window {
        lmSMTObj: LmSMTObj;
        dataLayer: Record<string, any>[];
    }

    interface ProductRoute {
        id: number,
        productroute: string,
        minAge: number,
        maxAge: number,
        routename: string,
        City: string
    }

}

export interface ViewPlanGenericProps {
    multi?: boolean;
    productType?: string;
    initData?: any;
    leadFormType: string;
    isds: Record<string, string>[];
}

const ViewPlanGeneric: FC<ViewPlanGenericProps> = ({
    multi,
    productType,
    initData,
    leadFormType,
    isds
}) => {
    const router = useRouter();
    const { cid, utm_source, utm_campaign, utm_term, utm_medium } = router.query;
    const retailFlag = initData == undefined ? "true" : "false";
    const [showOTP, setShowOTP] = useState(false);
    const [otp, setOTP] = useState("");
    const [isOtpVerified, setIsVerifiedOtp] = useState(false);
    const [previousPhoneNumber, setPreviousPhoneNumber] = useState("");

    const initialFormState = {
        ...initData,
        name: "",
        phoneNumber: "",
        dob: "",
        city: "",
        port: "",
        country: defaultCountry,
        subDepartment: initData?.agent !== "" ? initData?.agent : ''
    };

    // Handle the 'retailFlag' transformation here
    if (!retailFlag && initialFormState.phoneNumber) {
        initialFormState.phoneNumber = (
            (parseInt(initialFormState.phoneNumber) - 931) / 77
        ).toString();
    }

    // Now, call the new, clean hook with the prepared initial state
    const [getQuoteForm, setGetQuoteForm] = useLocalStorageNew(
        retailFlag === "true"
            ? StorageKeys.GET_QUOTE_FORM
            : StorageKeys.TELE_QUOTE_FORM,
        initialFormState
    );

    const constraint =
        productType && Object.hasOwn(constraints, productType)
            ? constraints[productType]
            : constraints["default"];

    const validateForm = (formState: typeof getQuoteForm) => {
        const newErrors: any = {};

        // Name
        if (!formState.name?.trim() || !nameRegex.test(formState.name)) {
            newErrors.name = "Please enter a valid name.";
        }

        // Phone
        const maxLength = Number(formState?.country?.maximum ?? 10);
        const minLength = Number(formState?.country?.minimum ?? 10);
        const phone = String(formState.phoneNumber ?? "").trim();

        if (!phone) {
            newErrors.phone = "Mobile number is required.";
        } else if (phone.length > maxLength) {
            newErrors.phone = `Please enter no more than ${maxLength} digits mobile number.`;
        } else if (phone.length < minLength) {
            newErrors.phone = `Please enter at least ${minLength} digits mobile number.`;
        } else {
            // length is between min and max inclusive -> validate format
            if (formState?.country?.ISD === "+91") {
                // keep your current India checks (only when length satisfies)
                if (
                    phone.length !== maxLength ||
                    phoneRegex.test(phone) ||
                    ["1", "2", "3", "4"].includes(phone.charAt(0))
                ) {
                    newErrors.phone = "Please enter a valid mobile number.";
                }
            } else {
                if (!validPhoneRegex(minLength, maxLength).test(phone)) {
                    newErrors.phone = "Please enter a valid mobile number.";
                }
            }
        }

        // DOB, city as before...
        if (leadFormType === LeadFormTypes.THREE || leadFormType === LeadFormTypes.FIVE) {
            if (!formState.dob) {
                newErrors.dob = "Please select a valid date";
            } else {
                const age = getAge(formState.dob);
                if (age < constraint.min || age >= constraint.max) {
                    newErrors.dob = `Please enter dob between ${constraint.min} & ${constraint.max - 1}`;
                }
            }
        }

        if (leadFormType === LeadFormTypes.FIVE && !formState.city?.trim()) {
            newErrors.city = "Please select your city";
        }

        return newErrors;
    };

    const [errors, setErrors] = useState(() => validateForm(getQuoteForm));

    const age = useMemo(() => {
        const curAge = getQuoteForm?.dob ? getAge(getQuoteForm?.dob) : 0;
        return curAge <= 100 ? curAge : 0;
    }, [getQuoteForm?.dob]);
    const { data: ipAddress } = useGetUserIP();
    const [time, setTime] = useState(59);
    const interval = useRef<any>(null);

    const {
        trigger: generateOTP,
        isMutating: generatingOTP,
        data: generateOTPData,
        reset: resetGenerateOTPData,
    } = usePhoneGenerateOTP({
        onSuccess: (data) => {
            setOTP("");
            if (data?.success) {
                if (interval.current) {
                    clearInterval(interval.current);
                    setTime(59);
                }
                interval.current = setInterval(() => {
                    setTime((prev) => {
                        if (prev > 0) {
                            return prev - 1;
                        } else {
                            interval.current && clearInterval(interval.current);
                            return 60;
                        }
                    });
                }, 1000);

                if ("OTPCredential" in window) {
                    const abortController = new AbortController();
                    setTimeout(() => {
                        abortController.abort();
                    }, 2 * 60 * 1000);
                    navigator.credentials
                        .get({
                            otp: { transport: ["sms"] },
                            signal: abortController.signal,
                        })
                        .then((otpCredential) => {
                            const otpCode = (otpCredential as any)?.code;
                            if (otpCode) {
                                setOTP(otpCode);
                            }
                        })
                        .catch((err) => {
                            console.error(err);
                        });
                }
            }
        },
    });

    const {
        trigger: validateOTP,
        isMutating: validatingOTP,
        data: validateOTPData,
        reset: resetValidateOTPData,
    } = usePhoneValidateOTP();


    const { trigger, isMutating } = usePostKyc({
        onSuccess: (data) => {
            console.info("LEAD CREATED ___>>>>", data);
        },
        onError: (error: any) => {
            console.error("Error in api call post kyc", error);
        },
    });

    const { data: cities, isLoading: isCitiesLoading, error } = useGetCities({});

    const isLoading = isMutating || generatingOTP || validatingOTP;

    const payload = {
        ipAddress: ipAddress?.ip,
        campaignId: cid as string,
        utmLeadFields: {
            utmSource: utm_source as string,
            utmCampaign: utm_campaign as string,
            utmTerm: utm_term as string,
            utmMedium: utm_medium as string,
        },
        channel: getQuoteForm.channel || "Retail",
        subChannel: getQuoteForm.subChannel || "Website",
        department: getQuoteForm.department || "SWebsite",
        subDepartment: getQuoteForm.subDepartment || "Website",
        agentName: getQuoteForm.agentName || "",
        language: "english",
        productId: "",
    };

    const corporatePayload = {
        utmLeadFields: {
          utmSource: (utm_source as string) || "",
          utmCampaign: (utm_campaign as string) || "",
          utmTerm: (utm_term as string) || "",
          utmMedium: (utm_medium as string) || "",
        },
        channel: "New Business",
        subChannel: "Website",
        source: "Website",
        department: "SWebsite",
        subDepartment: "Website",
        language: "english",
        campaignId: isOtpVerified ? "CSG Verified" : "CSG Non Verified",
        productId: "CSG",
      };
  

    const onMobileReset = () => {
        setGetQuoteForm((prev) => ({ ...prev, phoneNumber: "" }));
    };

    const onMobileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const digits = String(raw ?? "").replace(/\D/g, "");
        const maxLength = Number(getQuoteForm?.country?.maximum ?? 10);
        const minLength = Number(getQuoteForm?.country?.minimum ?? 1);

        if (validateOTPData) resetValidateOTPData();
        if (generateOTPData) resetGenerateOTPData();

        if (showOTP && digits !== previousPhoneNumber) {
            setShowOTP(false);
            setOTP("");
            if (interval.current) {
                clearInterval(interval.current);
                interval.current = null;
            }
            setTime(59);
        }

        // prevent typing beyond max but still keep UX predictable
        if (digits.length > maxLength) {
            setErrors(prev => ({ ...prev, phone: `Please enter no more than ${maxLength} digits mobile number` }));
            // do not update stored phoneNumber beyond max
            return;
        }

        setGetQuoteForm(prev => ({ ...prev, phoneNumber: digits }));
        setPreviousPhoneNumber(digits);

        if (!digits.length) {
            setErrors(prev => ({ ...prev, phone: "Mobile number is required." }));
            return;
        }

        if (digits.length < minLength) {
            setErrors(prev => ({ ...prev, phone: `Please enter at least ${minLength} digits mobile number` }));
            return;
        }

        // digits between min and max -> clear length error and only validate format
        if (digits.length <= maxLength) {
            if (
                getQuoteForm?.country?.ISD === "+91" &&
                (phoneRegex.test(digits) || ["1", "2", "3", "4"].includes(digits.charAt(0)))
            ) {
                setErrors(prev => ({ ...prev, phone: "Please enter the valid mobile number" }));
                return;
            }

            if (digits.length >= minLength && digits.length <= maxLength && !validPhoneRegex(minLength, maxLength).test(digits)) {
                setErrors(prev => ({ ...prev, phone: "Please enter the valid mobile number" }));
                return;
            }

            setErrors(prev => ({ ...prev, phone: undefined }));
            return;
        }
    };
      
    useEffect(() => {
        if (getQuoteForm) {
            const validationErrors = validateForm(getQuoteForm);
            setErrors(validationErrors);
        }
    }, [getQuoteForm, leadFormType]);

    // Track initial phone number for comparison
    useEffect(() => {
        if (getQuoteForm?.phoneNumber && !previousPhoneNumber) {
            setPreviousPhoneNumber(getQuoteForm.phoneNumber);
        }
    }, [getQuoteForm?.phoneNumber]);

    const handleOTP = async (val: string) => {
        setOTP(val);
        setErrors(prev => ({ ...prev, otp: undefined }));
        if (!/^\d*$/.test(val)) return;
        if (val.length !== 4) return;
        try {
            const data = await validateOTP({ otp: val, phoneNumber: getQuoteForm.phoneNumber });
            if (data?.success) {
                setIsVerifiedOtp(true);
                onViewPlan(true);
            } else {
                setErrors(prev => ({ ...prev, otp: data?.message || "Incorrect OTP. Please try again." }));
            }
        } catch (err: any) {
            const msg =
                err?.data?.message ||
                err?.response?.data?.message ||
                err?.message ||
                "Network error, please retry";
            setErrors(prev => ({ ...prev, otp: msg }));
        }
    };

    const onViewPlan = async (isOtpVerified = false, isGetOtpFlow = false) => {
        const formBody = {
            country: {
                isd: getQuoteForm?.country?.ISD,
                name: getQuoteForm?.country?.country,
            },
            isGodsEyeServiceCall: false,
        };

        if (getQuoteForm?.country?.ISD === "+91") {
            formBody["phoneNumber"] = getQuoteForm?.phoneNumber;
        } else {
            formBody["intPhoneNumber"] = getQuoteForm?.phoneNumber;
        }

        corporatePayload.campaignId = isOtpVerified ? "CSG Verified" : "CSG Non Verified";
        const finalPayload = leadFormType === LeadFormTypes.CORPORATE ? corporatePayload : payload;
        const requestData = {
            ...finalPayload,
            forms: {
                ...formBody,
                name: getQuoteForm?.name?.toUpperCase() || "",
                dateOfBirth: getQuoteForm?.dob || "",
                city: getQuoteForm?.city || "",
                port: getQuoteForm?.port || "",
                getQuickQuoteForm: {
                    "adultsDob": [],
                    "ageOfEldestMember": age,
                    "childsDob": [],
                    "dateofBirthOfEldestMember": getQuoteForm.dob,
                    "familyCombination": "1A",
                    "geography": getQuoteForm?.city || "",
                    "noOfAdults": 1,
                    "noOfChildren": 0,
                    "noOfMembers": 1,
                    "planType": "individual",
                    "showModal": true,
                    "protabiltyDetails": {}
                },
            },
            cookies: { ga: getCookie("_ga") },
        };


        trigger(
            {
                data: requestData,
            },
            {
                onSuccess: async (data) => {
                    if (data?.success && data?.quoteId && data?.dialerId) {
                        sessionStorage.setItem(StorageKeys.QUOTE_ID, data.quoteId);
                        sessionStorage.setItem(StorageKeys.DIALER_ID, data.dialerId);
                        if(isGetOtpFlow) return;

                        const queryParams = {
                            multi: "true",
                            productType: "reassurev3",
                            retailFlag,
                            isRelog: router.query.isRelog,
                            copay: router.query.copay,
                        };

                        if (leadFormType === LeadFormTypes.CORPORATE) {
                            const encryptedPhoneNumberRequest = await fetch(
                                "/api/corporate-insurance/get-encrypted-phonenumber",
                                {
                                    method: "POST",
                                    headers: {
                                        "Content-Type": "application/json",
                                    },
                                    body: JSON.stringify({
                                        text: (formBody["phoneNumber"] || getQuoteForm.phoneNumber).toString(),
                                    }),
                                }
                            );
                            const encryptedPhoneNumber = await encryptedPhoneNumberRequest.json();
                            router.push(
                                `https://instasaral.nivabupa.com/abacusplus/?phoneNumber=${encryptedPhoneNumber.encryptedText}`
                            );
                        } else {

                            router.push({
                                pathname: "/health-insurance-plan/summary",
                                query: queryParams,
                            });
                        }
                    } else {
                        console.warn("Quote API did not return success or missing quoteId/dialerId");
                    }
                },
                onError: (error) => {
                    console.error("Quote API error:", error);
                },
            }
        );
    };

    const onCountryChange = (countryObj: any) => {
        setGetQuoteForm((prev) => ({
            ...prev,
            country: countryObj,
            phoneNumber: "",
        }));

        if (showOTP) {
            setShowOTP(false);
            setOTP("");
            if (interval.current) {
                clearInterval(interval.current);
            }
            setTime(59);
        }
        setPreviousPhoneNumber("");
    }

    const onNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        const name = e.target.value.trimStart();
        setErrors(prev => ({ ...prev, name: undefined }));
        setGetQuoteForm(prev => ({ ...prev, name }));

        if (!name || !nameRegex.test(name)) {
            setErrors(prev => ({ ...prev, name: "Please enter a valid name." }));
        } else {
            setErrors(prev => {
                const { name, ...rest } = prev;
                return rest;
            });
        }
    };

    const onDateChangeDesktop = (e: ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value;
        // Only update the state. The useEffect will handle validation.
        setGetQuoteForm(prev => ({ ...prev, dob: rawValue }));
    };

    const onDateChangeMobile = (value: string) => {
        // Only update the state. The useEffect will handle validation.
        setGetQuoteForm(prev => ({ ...prev, dob: value }));
    };

    const onCityChange = (val: string) => {
        const citySelected = val.trim().toLowerCase();
        // Only update the state. The useEffect will handle validation.
        setGetQuoteForm(prev => ({ ...prev, city: citySelected }));
    };

    const onPortChange = (value: string) => {
        setGetQuoteForm(prev => ({ ...prev, port: value }));
    };

    useEffect(() => {
        localStorage.setItem(
            "homeInfo",
            JSON.stringify({ insuranceType: "Health" })
        );
    }, []);

    const onGetOTP = async () => {
        const phone = getQuoteForm?.phoneNumber ?? "";
        const maxLength = Number(getQuoteForm?.country?.maximum ?? 10);
        const minLength = Number(getQuoteForm?.country?.minimum ?? 1);
      
        if (!phone || phone.length < minLength || phone.length > maxLength) {
          setErrors(prev => ({ ...prev, phone: `Please enter a valid phone number between ${minLength} and ${maxLength} digits` }));
          return;
        }
      
        if (!validPhoneRegex(minLength, maxLength).test(phone)) {
          setErrors(prev => ({ ...prev, phone: "Invalid phone number" }));
          return;
        }
    
        // Clear previous errors before making a new request
        setErrors(prev => ({ ...prev, phone: undefined }));
    
        try {
            const data = await generateOTP({ phoneNumber: phone });
    
            if (data?.success) {
                setShowOTP(true);
                setPreviousPhoneNumber(phone);
                setTime(60);
                onViewPlan(false, true);
    
                if (interval.current) clearInterval(interval.current);
                interval.current = setInterval(() => {
                    setTime(t => {
                        if (t > 0) return t - 1;
                        clearInterval(interval.current!);
                        return 0;
                    });
                }, 1000);
    
                if (data.message) {
                    setErrors(prev => ({ ...prev, phone: data.message }));
                }
            } else {
                const errorMessage = data?.message || "Failed to send OTP. Try again.";
                setErrors(prev => ({ ...prev, phone: errorMessage }));
            }
        } catch (err) {
            console.error("A network or parsing error occurred:", err);
            setErrors(prev => ({ ...prev, phone: "Network error. Please retry." }));
        }
    };
    
    const handleBackspace = () => {
        setOTP((prev) => prev.slice(0, -1));
    };

    const leadFormComponents: Record<string, JSX.Element> = {
        [LeadFormNames[LeadFormTypes.TWO]]:
            <TwoLeadForm
                showOTP={showOTP}
                isds={isds}
                onCountryChange={onCountryChange}
                onMobileReset={onMobileReset}
                initData={initData}
                onMobileChange={onMobileChange}
                onGetOTP={onGetOTP}
                otp={otp}
                getQuoteForm={getQuoteForm}
                onViewPlan={onViewPlan}
                time={time}
                handleOTP={handleOTP}
                onNameChange={onNameChange}
                errors={errors}
                isLoading={isLoading}
                formName="two-lead-form"
                handleBackspace={handleBackspace}
            />,
        [LeadFormNames[LeadFormTypes.THREE]]:
            <ThreeLeadForm
                isds={isds}
                onCountryChange={onCountryChange}
                onMobileReset={onMobileReset}
                initData={initData}
                onMobileChange={onMobileChange}
                getQuoteForm={getQuoteForm}
                onViewPlan={onViewPlan}
                errors={errors}
                onNameChange={onNameChange}
                isLoading={isLoading}
                constraint={constraint}
                onDateChange={onDateChangeDesktop}
                onDateChangeMobile={onDateChangeMobile}
                formName="tw-lead-form"
            />,
        [LeadFormNames[LeadFormTypes.FIVE]]:
            <FiveLeadForm
                isds={isds}
                onCountryChange={onCountryChange}
                onMobileReset={onMobileReset}
                initData={initData}
                onMobileChange={onMobileChange}
                getQuoteForm={getQuoteForm}
                isLoading={isLoading}
                onViewPlan={onViewPlan}
                errors={errors}
                onNameChange={onNameChange}
                onDateChange={onDateChangeDesktop}
                constraint={constraint}
                cities={cities}
                onCityChange={onCityChange}
                onPortChange={onPortChange}
                formName="five-lead-form"
            />,
        [LeadFormNames[LeadFormTypes.CORPORATE]]:
            <CorporateLeadForm
                showOTP={showOTP}
                isds={isds}
                onCountryChange={onCountryChange}
                onMobileReset={onMobileReset}
                initData={initData}
                onMobileChange={onMobileChange}
                onGetOTP={onGetOTP}
                otp={otp}
                getQuoteForm={getQuoteForm}
                onViewPlan={onViewPlan}
                time={time}
                handleOTP={handleOTP}
                errors={errors}
                isLoading={isLoading}
                formName="corporate-lead-form"
            />,
    };

    const SelectedLeadForm = leadFormComponents[leadFormType] || null;

    return <>{SelectedLeadForm}</>;
};

export default ViewPlanGeneric;